import { InjectionToken } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Observable } from 'rxjs';

/**
 * Interface that a control must implement to work with HxFormGroup. Based on Angular Material's MatFormFieldControl.
 *
 * This allows any control (input, textarea, select, custom components) to integrate with HxFormGroup's label, hint, and
 * error display.
 *
 * @Directive({
 *   selector: 'textarea[hxTextarea]',
 *   providers: [{ provide: HX_FORM_FIELD_CONTROL, useExisting: HxTextarea }]
 * })
 * export class HxTextarea implements HxFormFieldControl<string> {
 *   // ... implementation
 * }
 *
 * @feature 011-harmony-form-field
 */
export type HxFormFieldControl<T> = {
  /** The value of the control. */
  readonly value: T | null;

  /** Stream that emits whenever the state of the control changes. */
  readonly stateChanges: Observable<void>;

  /** The element ID for this control. */
  readonly id: string;

  /** Whether the control is focused. */
  readonly focused: boolean;

  /** Whether the control is empty. */
  readonly empty: boolean;

  /** Whether the control is in an error state. */
  readonly errorState: boolean;

  /** Whether the control is disabled. */
  readonly disabled: boolean;

  /** Whether the control is required. */
  readonly required: boolean;

  /** Whether the label should float. */
  readonly shouldLabelFloat: boolean;

  /** The placeholder for this control. */
  readonly placeholder: string;

  /** Gets the AbstractControl for this control (from NgControl). Optional. */
  readonly ngControl?: NgControl | null;

  /** Sets the list of element IDs that currently describe this control. */
  setDescribedByIds(ids: string[]): void;

  /** Handles a click on the control's container. */
  onContainerClick(event: MouseEvent): void;
};

/** Injection token for HxFormFieldControl. Use this to provide a control implementation to HxFormGroup. */
export const HX_FORM_FIELD_CONTROL = new InjectionToken<HxFormFieldControl<any>>('HX_FORM_FIELD_CONTROL');
